import { useState, useEffect, useRef } from "react";
import { ArrowLeft } from 'lucide-react';
import { authService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { MediaManager } from "../../../components/MediaManager/MediaManager";
import { TextForm } from "../../../components/TextForm/TextForm";
import { validateFields } from "../../../utils/contentBlacklistValidator";
import PersonalizedOrderBump from "../OrderBump/PersonalizedOrderBump";
import DeliveryTypeField from "../../../components/UI/DeliveryTypeSelect/DeliveryTypeField";
import { DELIVERY_TYPE_META } from "../../../utils/deliveryTypeMeta";
import { applyOnlyUpsellRule } from "../../../utils/gateways";
import { useOnlyUpsellBlocked } from "../../../hooks/useOnlyUpsellBlocked";

// `default` = usa a entrega GLOBAL dos downsells (configurada na lista); essa,
// por sua vez, cai no fluxo principal do bot se também estiver em padrão.
const DOWNSELL_DELIVERY_OPTIONS = [
    { value: 'default', label: 'Usar Entrega Global dos Downsells', ...DELIVERY_TYPE_META.default },
    { value: 'vip_group', label: 'Grupo VIP', ...DELIVERY_TYPE_META.vip_group },
    { value: 'message_only', label: 'Apenas mensagem', ...DELIVERY_TYPE_META.message_only },
    { value: 'external_link', label: 'Link externo', ...DELIVERY_TYPE_META.external_link },
    { value: 'only_upsell', label: 'Apenas Upsell', ...DELIVERY_TYPE_META.only_upsell },
];

export default function DownsellForm({ botId, editing, onEditingChange, onSuccess, onBack }) {
    const onlyUpsellBlocked = useOnlyUpsellBlocked(botId);
    const { showError, showSuccess } = useNotification();
    const mediaRef = useRef();
    const bumpRef = useRef();

    const [form, setForm] = useState({
        id: null,
        name: "",
        message: "",
        discount_percentage: 0,
        delay_minutes: 10,
        is_active: true,
        delivery: null,
    });

    const [mediaList, setMediaList] = useState([]);
    const [sendMode, setSendMode] = useState('agrupado');

    const [loading, setLoading] = useState(false);




    useEffect(() => {
        if (editing) {
            setForm({
                id: editing.id,
                name: editing.name,
                message: editing.message,
                discount_percentage: editing.discount_percentage,
                delay_minutes: editing.delay_minutes,
                is_active: editing.is_active,
                delivery: editing.delivery || null,
            });

            setSendMode(editing.media_send_mode || 'agrupado');

            if (editing.media_list && editing.media_list.length > 0) {
                setMediaList(editing.media_list);
            } else {
                const legacy = [];
                if (editing.image_file_id) legacy.push({ media_type: 'photo', file_id: editing.image_file_id, thumbnail_file_id: null });
                if (editing.video_file_id) legacy.push({ media_type: 'video', file_id: editing.video_file_id, thumbnail_file_id: null });
                if (editing.audio_file_id) legacy.push({ media_type: 'voice', file_id: editing.audio_file_id, thumbnail_file_id: null });
                setMediaList(legacy);
            }


        } else {
            resetForm();
        }
    }, [editing]);

    const resetForm = () => {
        setForm({
            id: null,
            name: "",
            message: "",
            discount_percentage: 0,
            delay_minutes: 10,
            is_active: true,
            delivery: null,
        });
        setMediaList([]);
        setSendMode('agrupado');
        onEditingChange(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const contentCheck = validateFields([
            { label: 'Nome da Oferta', value: form.name },
            { label: 'Mensagem do Downsell', value: form.message },
        ]);
        if (!contentCheck.isValid) {
            showError(contentCheck.error);
            return;
        }

        // Valida o Order Bump ANTES de criar o downsell (se inválido, nem cria a oferta).
        const bumpErr = bumpRef.current?.validate?.();
        if (bumpErr) {
            showError(bumpErr);
            return;
        }

        setLoading(true);

        try {
            // 1️⃣ Salvar downsell
            const { data } = await authService.downsell.save_downsell(botId, { ...form, media_send_mode: sendMode });

            if (!data.success) throw new Error(data.error);

            // 2️⃣ Pegar o ID (criar ou editar)
            const downsellId = data.offer_id || form.id;
            // 3️⃣ Upload da mídia do downsell
            if (mediaRef.current && downsellId) {
                await mediaRef.current.uploadAll(downsellId);
            }

            // 4️⃣ Order bump personalizado — salvo JUNTO, já com a oferta criada.
            try {
                await bumpRef.current?.save?.(downsellId);
            } catch (e) {
                showError(e?.response?.data?.message || e.message || "Erro ao salvar Order Bump");
                return;
            }

            showSuccess("Downsell salvo com sucesso!");
            resetForm();
            onSuccess();
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <header style={{ marginBottom: 24 }}>
                <div class="flex items-center gap-3">
                    <button onClick={onBack} type="button" class="p-2 rounded-lg bg-white/[0.02] border border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.04] transition-colors cursor-pointer"><ArrowLeft size={20} /></button>
                    <div>
                        <h1 className='text-2xl font-bold text-white'>{form.id ? "Editar Downsell" : "Novo Downsell"}</h1>
                        <p class="text-sm text-white/40 mt-0.5">Quando ativo, todos os usuários que derem "/start" no bot receberão a mensagem de downsell</p>
                    </div>
                </div>
            </header>

            <form onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>

                {/* Nome */}
                <div className="mt-4">
                    <label className="spt-label">Nome da Oferta <span className="required">*</span></label>
                    <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        className="input3"
                        placeholder="Ex: Downsell - Oferta 1"
                    />
                    <small className="form-hint">
                        Nome da oferta para identificação interna. Não é mostrado para os usuários.
                    </small>
                </div>

                {/* MEDIA */}
                <div className="mt-8">
                    <MediaManager
                        ref={mediaRef}
                        botId={botId}
                        targetType="offer"
                        targetId={form.id}
                        multiMedia={true}
                        mediaList={mediaList}
                        onMediaChange={setMediaList}
                        sendMode={sendMode}
                        onSendModeChange={setSendMode}
                        uploadContext="downsell"
                    />
                </div>

                {/* Mensagem */}
                <div className="mt-8">
                    <label className="spt-label">Mensagem do Downsell <span className="required">*</span></label>

                    <TextForm value={form.message} onChange={(value) => setForm({ ...form, message: value })} hint="Mensagem que será exibida ao cliente, apresentando a oferta do downsell." showVariables={false} />

                </div>

                {/* Desconto */}
                <div className="flex gap-8 mx-auto justify-center align-center">
                    <div className="mt-8 w-1/2" >
                        <label className="spt-label">Desconto<span className='required'>*</span></label>
                        <select
                            className="form-select"
                            value={form.discount_percentage}
                            onChange={(e) =>
                                setForm({ ...form, discount_percentage: Number(e.target.value) })
                            }
                        >
                            {[0, 10, 15, 20, 25, 30, 40, 50, 60].map(v => (
                                <option key={v} value={v}>
                                    {v === 0 ? "Sem desconto" : `${v}%`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Delay */}
                    <div className="mt-8 w-1/2" >
                        <label className="spt-label">Tempo para o envio<span className='required'>*</span></label>
                        <select
                            required
                            className="form-select"
                            value={form.delay_minutes}
                            onChange={(e) =>
                                setForm({ ...form, delay_minutes: Number(e.target.value) })
                            }
                        >
                            {[
                                { v: 5, label: "5 minutos" },
                                { v: 10, label: "10 minutos" },
                                { v: 15, label: "15 minutos" },
                                { v: 30, label: "30 minutos" },
                                { v: 45, label: "45 minutos" },
                                { v: 60, label: "1 hora" },
                                { v: 120, label: "2 horas" },
                                { v: 180, label: "3 horas" },
                                { v: 300, label: "5 horas" },
                                { v: 360, label: "6 horas" },
                                { v: 720, label: "12 horas" },
                                { v: 1440, label: "1 dia" },
                                { v: 2880, label: "2 dias" },
                                { v: 4320, label: "3 dias" },
                                { v: 5760, label: "4 dias" },
                                { v: 7200, label: "5 dias" },
                                { v: 8640, label: "6 dias" },
                                { v: 10080, label: "7 dias" }
                            ].map(opt => (
                                <option key={opt.v} value={opt.v}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Entrega deste downsell */}
                <div className="mt-8 max-w-md">
                    <label className="spt-label">Entrega deste Downsell</label>
                    <DeliveryTypeField
                        value={form.delivery}
                        onChange={(delivery) => setForm({ ...form, delivery })}
                        options={applyOnlyUpsellRule(DOWNSELL_DELIVERY_OPTIONS, onlyUpsellBlocked)}
                    />
                </div>

                {/* Order bump personalizado — aparece desde a criação; salvo
                    junto com o downsell (o próprio botão "Salvar" abaixo). */}
                <div className="mt-8">
                    <PersonalizedOrderBump
                        ref={bumpRef}
                        botId={botId}
                        offerId={form.id}
                        uploadContext="downsell_orderbump"
                    />
                </div>

                {/* BOTÕES */}
                <div className="flex gap-4 mt-6 justify-end">
                    <button type="button" onClick={onBack} className="btn-secondary">
                        <ArrowLeft size={16} className="inline" />Voltar
                    </button>
                    <button disabled={loading} className="btn-primary">
                        {loading ? "Salvando..." : "Salvar"}
                    </button>
                </div>

            </form>
        </>
    );
}
